import { Box, Button, ButtonGroup, Heading, Image, Paragraph, Text, TextButton } from '@zillow/constellation-10';

import NotificationContainer from '@common/components/NotificationContainer';
import Page from '@renter-hub/components/RenterPage';
import zillowAndPlaidSvg from '@assets/zillow-plaid.svg';
import FaqComponent from '@common/components/ConstellationFaqComponent/index';
import PaymentMethodModule from '@renter-hub/components/PaymentMethodModule';
import HelmetContainer from '@common/components/HelmetContainer';
import PlaidButton from '@common/components/PlaidButton';
import SeoConstants from '@renter-hub/constants/seoConstants';
import userPersonas from '@common/constants/userPersonas';
import { RenterConfigInterface, RenterPaymentMethodInterface } from '@renter-hub/models/Types';
import { VERIFICATION_STATUS } from '@common/constants/PlaidRequirement';
import { useCreateRenterPaymentMethod } from '@renter-hub/hooks/useCreatePaymentMethod';

const verificationTypeMap = {
  [VERIFICATION_STATUS.PENDING_AUTOMATIC.INTERNAL]: {
    heading: 'Verification in progress',
    subheading:
      'Your account will be automatically verified by Plaid in approximately one day. No action is needed on your part.',
  },
  [VERIFICATION_STATUS.PENDING_MANUAL.INTERNAL]: {
    heading: 'Verify your bank account',
    subheading: 'Finish verifying with Plaid, our trusted partner, to securely add your bank account.',
  },
  [VERIFICATION_STATUS.VERIFICATION_FAILED]: {
    heading: 'Verification failed',
    subheading:
      "We unfortunately couldn't verify your bank account with Plaid. Retry linking and verifying your bank account. Once verified, you can start paying your rent online through Zillow.",
  },
};

interface PlaidMicrodepositsPageProps {
  config: RenterConfigInterface;
  getPlaidUpdateModeLinkToken: (externalId?: string) => any;
  onEditPaymentInfo: () => void;
  onPaymentMethodCreationSuccess: (paymentMethod: any) => Promise<any>;
  onPaymentMethodCreationError: (error: unknown) => Promise<any>;
  trackValidationError: (...args: any) => any;
  routeToDashboard: () => any;
  mostRecentAccountNeedingVerification: RenterPaymentMethodInterface;
}

const PlaidMicrodepositsPage = ({
  config,
  getPlaidUpdateModeLinkToken,
  onEditPaymentInfo,
  mostRecentAccountNeedingVerification,
  trackValidationError,
  onPaymentMethodCreationSuccess,
  onPaymentMethodCreationError,
  routeToDashboard,
}: PlaidMicrodepositsPageProps) => {
  const { accountLast4, paymentMethodStatus, displayName: accountName } = mostRecentAccountNeedingVerification;

  const { mutateAsync: createRenterPaymentMethod } = useCreateRenterPaymentMethod({
    onSuccess: async (response) => {
      await onPaymentMethodCreationSuccess(response.paymentMethod);
    },
    onError: async (error) => {
      await onPaymentMethodCreationError(error);
    },
  });

  if (
    !paymentMethodStatus ||
    paymentMethodStatus === VERIFICATION_STATUS.VERIFIED ||
    paymentMethodStatus === VERIFICATION_STATUS.UNKNOWN
  ) {
    routeToDashboard();
    return;
  }

  const onPlaidExit = (err, metadata) => {
    trackValidationError({
      plaidErrorsLastEncountered: err,
      plaidMetadata: metadata,
    });
  };

  const handlePlaidSuccess = async (data) => {
    await createRenterPaymentMethod({
      postData: {
        plaidToken: {
          accountId: data.accountId,
          publicToken: data.publicToken,
        },
        bankName: data.bankName,
        mask: data.mask,
        ...(data.status && { status: data.status }),
      },
    });
  };

  const renderFaqSection = (withBorder = true) => (
    <FaqComponent
      faqs={[
        {
          question: 'What is Plaid™?',
          answer:
            'Thousands of companies, including Zillow, use Plaid to authenticate accounts and keep transactions secure.',
        },
        {
          question: 'How verification works',
          answer:
            "You'll receive a one-time micro-deposit in your bank account. Zillow will then ask you to confirm a detail from that transaction to verify your account. If you can't use micro-deposits, there are other verification methods available.",
        },
      ]}
      withBorder={withBorder}
    />
  );
  return (
    <Page>
      <HelmetContainer {...SeoConstants.microDepositsPage} />
      <Page.MainSection>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'token(spacing.md)' }}>
          <NotificationContainer persona={userPersonas.RENTER} />
          <Image alt="plaid logo" src={zillowAndPlaidSvg as unknown as string} css={{ width: '82px' }} />
          <Heading level={1} textStyle="heading-sm" data-test="MicroDeposits">
            {verificationTypeMap[paymentMethodStatus].heading}
          </Heading>
          <Paragraph>{verificationTypeMap[paymentMethodStatus].subheading}</Paragraph>
          <Box css={{ marginBottom: 'token(spacing.md) !important' }}>
            <PaymentMethodModule
              accountName={accountName}
              accountLast4={accountLast4}
              onClick={onEditPaymentInfo}
              editable
            />
          </Box>
          {paymentMethodStatus === VERIFICATION_STATUS.PENDING_AUTOMATIC.INTERNAL ? (
            <Text>
              Don't want to wait?{' '}
              <TextButton css={{ textDecoration: 'none' }} onClick={onEditPaymentInfo}>
                Pay with a card
              </TextButton>
            </Text>
          ) : (
            <ButtonGroup aria-label="verification-methods" role="group" fluid>
              {paymentMethodStatus === VERIFICATION_STATUS.PENDING_MANUAL.INTERNAL && (
                <PlaidButton
                  plaidEnv={config.plaidEnv}
                  plaidLinkToken={config.plaidLinkToken}
                  plaidLinkTokenExpiration={config.plaidLinkTokenExpiration}
                  getPlaidLinkToken={() =>
                    getPlaidUpdateModeLinkToken(
                      'id' in mostRecentAccountNeedingVerification
                        ? mostRecentAccountNeedingVerification.id
                        : undefined,
                    )
                  }
                  label="Verify with Plaid"
                  full={false}
                  onSuccess={handlePlaidSuccess}
                  onExit={onPlaidExit}
                  useConstellationButton={true}
                />
              )}
              <Button onClick={onEditPaymentInfo}>Pay with a card</Button>
            </ButtonGroup>
          )}
        </Box>
      </Page.MainSection>
      <Page.RightSection>{renderFaqSection()}</Page.RightSection>
      <Page.BottomSection>{renderFaqSection(false)}</Page.BottomSection>
    </Page>
  );
};

export default PlaidMicrodepositsPage;
